// components/Editor.tsx
import React, {useCallback, useRef, useState} from "react";
import {Editor, EditorContent, useEditor} from "@tiptap/react";
import Bold from "../images/Formatter Icons/format_bold.svg";
import Italic from "../images/Formatter Icons/format_italic.svg";
import indentDecrease from "../images/Formatter Icons/format_indent_decrease.svg";
import indentIncrease from "../images/Formatter Icons/format_indent_increase.svg";
import LeftAlign from "../images/Formatter Icons/format_align_left.svg";
import RightAlign from "../images/Formatter Icons/format_align_right.svg";
import CenterAlign from "../images/Formatter Icons/format_align_center.svg";

import Justify from "../images/Formatter Icons/Justification.svg";
import ColorImage from "../images/Formatter Icons/text_Color.svg";
import Attachment from "../images/Formatter Icons/attach_file.svg";

import Pdf from "../images/DownloadPdf.svg";
import Image from "../images/DownloadImage.svg";
import Doc from "../images/WordDocument.svg";
import Xls from "../images/Excel.svg";
import Close from "../images/Close.svg";

import {
  Props,
  stateType,
} from "../components/modules/asset/Meetings/planning/Plan";
import {allowedFormatsForMom} from "../utils/constants";
import {CustomModal, downloadFile, getFileIcon} from "../utils/UtilFunctions";
import StarterKit from "@tiptap/starter-kit";
import Paragraph from "@tiptap/extension-paragraph";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Text from "@tiptap/extension-text";
import Document from "@tiptap/extension-document";
import {ViewMoreFileStyles} from "./CustomModalStyles";
import {Title} from "../stories/Title/Title";
import {Button} from "../stories/Button/Button";

export interface editFileStateType {
  setEditSelectedFiles: React.Dispatch<React.SetStateAction<File[]>>;
  editSelectedFiles: File[];
}

export interface EditTipTapProps {
  commentState: stateType;
  filesState: editFileStateType;
  editor: Editor | null;
}

const EditTiptapEditor = ({
  commentState,
  editor,
  filesState,
}: EditTipTapProps) => {
  const {comment, setComment} = commentState;
  const {editSelectedFiles, setEditSelectedFiles} = filesState;
  const [selectedColor, setSelectedColor] = useState("#000000");
  const [moreFiles, setMoreFiles] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const some = useEditor({
    extensions: [
      StarterKit,
      Document,
      Paragraph,
      Text,
      BulletList,
      OrderedList,
      ListItem,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      TextStyle,
      Color,
    ],
    onUpdate: ({editor}) => {
      // Get HTML content and store in state
      setComment(editor.getHTML());
    },
    content: comment,
  });

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const color = e.target.value;
    setSelectedColor(color);
    if (editor) {
      editor.chain().focus().setColor(color).run();
    }
    e.preventDefault();
  };

  const clickFileInput = () => {
    const inputFile = document.getElementsByName("edit-attachment-input");
    if (inputFile && inputFile.length > 0) {
      inputFile[0].click();
    }
  };
  const chooseFile = useCallback(
    (file: File, index: number) => {
      const ext = file.name.split(".").pop();

      const ReturnComp = () => {
        return (
          <>
            {index < 2 ? (
              <div className="bg-grey" onClick={() => downloadFile(file)}>
                {file.name} <img src={getFileIcon(file.name)} alt="not found" />
                <img
                  src={Close}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditSelectedFiles((prev) =>
                      prev.filter((f, i) => i !== index)
                    );
                  }}
                  alt="not found"
                />
              </div>
            ) : index === 2 ? (
              <button
                className="case-study-list--more"
                onClick={() => {
                  setMoreFiles(true);
                }}
              >
                +{editSelectedFiles.length - 2} more...
              </button>
            ) : null}
          </>
        );
      };
      return <ReturnComp />;
    },
    [editSelectedFiles]
  );

  const handleChooseFilesForEdit = (e: React.ChangeEvent<HTMLInputElement>) => {
    let filesCannotbeAdded: File[] = [];
    let filesThatCanBeAdded: File[] = [];

    if (e.target.files && e.target.files.length) {
      const files = Array.from(e.target.files);
      files.forEach((file) => {
        if (allowedFormatsForMom.includes(file.name.split(".")[1])) {
          filesThatCanBeAdded.push(file);
        } else {
          filesCannotbeAdded.push(file);
        }
      });
    }
    setEditSelectedFiles([...filesThatCanBeAdded, ...editSelectedFiles]);

    if (filesCannotbeAdded.length) {
      alert(
        `Files ${filesCannotbeAdded
          .map((file) => file.name)
          .join(", ")} cannot be added`
      );
    }
    e.preventDefault();
  };

  if (!editor) {
    return null;
  }

  return (
    <div className="tip-tap-editor">
      <EditorContent
        editor={editor}
        className="tip-tap-editor__content"
        content={comment}
      />
      <div className={`tip-tap-editor__attachment`}>
        {editSelectedFiles
          ? editSelectedFiles.map((file, index) => chooseFile(file, index))
          : null}
      </div>
      <div className="tip-tap-editor__menu">
        <button
          onClick={(e) => {
            e.preventDefault();
            editor?.chain().focus().toggleBold().run();
          }}
          disabled={!editor?.can().chain().focus().toggleBold().run()}
        >
          <img src={Bold} alt="" />
        </button>
        <button
          onClick={(e) => {
            editor?.chain().focus().toggleItalic().run();
            e.preventDefault();
          }}
          disabled={!editor?.can().chain().focus().toggleItalic().run()}
        >
          <img src={Italic} alt="" />
        </button>
        <button
          onClick={(e) => {
            editor?.chain().focus().toggleHeading({level: 1}).run();
            e.preventDefault();
          }}
          className={editor?.isActive("heading", {level: 1}) ? "is-active" : ""}
        >
          h1
        </button>
        <button
          onClick={(e) => {
            editor?.chain().focus().toggleHeading({level: 1}).run();
            e.preventDefault();
          }}
          className={editor?.isActive("heading", {level: 2}) ? "is-active" : ""}
        >
          h2
        </button>
        <button
          onClick={(e) => {
            editor?.chain().focus().toggleHeading({level: 3}).run();
            e.preventDefault();
          }}
          className={editor?.isActive("heading", {level: 3}) ? "is-active" : ""}
        >
          h3
        </button>
        <button
          onClick={(e) => {
            editor?.chain().focus().setParagraph().run();
            e.preventDefault();
          }}
        >
          P
        </button>
        {/* <button
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
        >
          <img src={BulletListImage} alt="" />{" "}
        </button>
        <button
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
        >
          <img src={OrderedLisImage} alt="" />{" "}
        </button> */}
        <button
          onClick={(e) => {
            e.preventDefault();
            editor?.chain().focus().sinkListItem("listItem").run();
          }}
        >
          <img src={indentIncrease} alt="" />{" "}
        </button>
        <button
          onClick={(e) => {
            e.preventDefault();
            editor?.chain().focus().liftListItem("listItem").run();
          }}
        >
          <img src={indentDecrease} alt="" />{" "}
        </button>
        {/* <button
          onClick={() => editor?.chain().focus().toggleStrike().run()}
          className={editor?.isActive("strike") ? "is-active" : ""}
        >
          <img src={Strike} alt="" />
        </button> */}

        <button
          onClick={(e) => {
            e.preventDefault();
            editor?.chain().focus().setTextAlign("left").run();
          }}
          className={editor?.isActive({textAlign: "left"}) ? "is-active" : ""}
        >
          <img src={LeftAlign} alt="" />
        </button>
        <button
          onClick={(e) => {
            e.preventDefault();
            editor?.chain().focus().setTextAlign("center").run();
          }}
          className={editor?.isActive({textAlign: "center"}) ? "is-active" : ""}
        >
          <img src={CenterAlign} alt="" />
        </button>
        <button
          onClick={(e) => {
            e.preventDefault();
            editor?.chain().focus().setTextAlign("right").run();
          }}
          className={editor?.isActive({textAlign: "right"}) ? "is-active" : ""}
        >
          <img src={RightAlign} alt="" />{" "}
        </button>

        <button
          onClick={(e) => {
            e.preventDefault();
            editor?.chain().focus().setTextAlign("justify").run();
          }}
          className={
            editor?.isActive({textAlign: "justify"}) ? "is-active" : ""
          }
        >
          <img src={Justify} alt="" />{" "}
        </button>
        <button
          onClick={(e) => {
            e.preventDefault();
            editor?.chain().focus().setTextAlign("justify").run();
          }}
          className={
            editor?.isActive({textAlign: "justify"}) ? "is-active" : ""
          }
        >
          <img src={Justify} alt="" />{" "}
        </button>

        <button
          onClick={(e) => {
            e.preventDefault();
            clickFileInput();
          }}
          className={
            editor?.isActive({textAlign: "justify"}) ? "is-active" : ""
          }
        >
          <img src={Attachment} alt="" />{" "}
        </button>
        <input
          type="color"
          value={selectedColor}
          onChange={handleColorChange}
          className="tip-tap-editor__menu--color-picker"
          ref={inputRef}
        />
        <img
          src={ColorImage}
          alt=""
          onClick={(e) => {
            e.preventDefault();
            inputRef.current?.click();
          }}
        />
        <input
          type="file"
          name="edit-attachment-input"
          multiple
          accept={allowedFormatsForMom.join(", ")}
          onChange={handleChooseFilesForEdit}
          className="tip-tap-editor__menu--input-attachment"
        />
      </div>
      <CustomModal
        style={ViewMoreFileStyles}
        isOpen={moreFiles}
        onRequestClose={() => setMoreFiles(!moreFiles)}
        contentComponent={
          <div className="case-study-list--more-files">
            <div className="case-study-list--more-files-t">
              <Title>Uploaded Files</Title>
              <img
                src={Close}
                alt=""
                onClick={() => setMoreFiles(!moreFiles)}
              />
            </div>
            <div className="case-study-list--more-files--block">
              {editSelectedFiles.map((files, index) => {
                const filename = files.name.split("/").pop();

                return (
                  <div className="case-study-list--attachments" key={index}>
                    <img src={getFileIcon(files.name)} alt="" />
                    <span> {filename}</span>
                  </div>
                );
              })}
            </div>
            <Button mode="cancel" onClick={() => setMoreFiles(!moreFiles)} />
          </div>
        }
      />
    </div>
  );
};

export default EditTiptapEditor;
